/* eslint-disable jsx-a11y/click-events-have-key-events */
import { iSponsor } from '@interfaces/interfaces';
import { useEffect, useState, useRef } from 'react';
import { Accordion, Alert, Button } from '@components/index';
import classnames from 'classnames';
import styles from './SearchResults.module.scss';
import styles2 from './SearchBox.module.scss';
import sideArrowActive from '../../images/sideArrowActive.svg';

const NUMBER_OF_RESULTS_ON_A_PAGE = 10;

interface iWDPResults {
  results: iSponsor[];
  searchInput: string;
  noResultsError: string;
  // eslint-disable-next-line react/require-default-props
  sponsorResultCount?: number | null;
  // eslint-disable-next-line react/require-default-props
  telehealthResultsCount?: number | null;
}

export default function SearchResults({ results, searchInput, noResultsError, sponsorResultCount = null, telehealthResultsCount = null }: iWDPResults): any {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [accordionsOpen, setAccordionOpen] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    // Initial check on mount
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // eslint-disable-next-line react/destructuring-assignment
  function getPaginatedResults(page: number): iSponsor[] {
    const startIndex = (page - 1) * NUMBER_OF_RESULTS_ON_A_PAGE;
    const endIndex = startIndex + NUMBER_OF_RESULTS_ON_A_PAGE;
    return results.slice(startIndex, endIndex);
  }

  const [paginatedResults, setPaginatedResults] = useState(getPaginatedResults(1));
  const [currentPage, setCurrentPage] = useState(1);
  const maxPageNumber = Math.ceil(results.length / NUMBER_OF_RESULTS_ON_A_PAGE);
  const pageNumbers: any = [];

  useEffect(() => {
    setCurrentPage(1);
    setPaginatedResults(getPaginatedResults(1));
  }, [results]);

  const handlePageNumberClick = (pageNum: any) => {
    if (Number(pageNum.id) === currentPage) return;
    setPaginatedResults(getPaginatedResults(Number(pageNum.id)));
    setCurrentPage(Number(pageNum.id));
    setTimeout(() => {
      targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleFirstLastClick = (e: any, pageNumber: any) => {
    setTimeout(() => {
      targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    setPaginatedResults(getPaginatedResults(pageNumber));
    setCurrentPage(pageNumber);
  };

  const handleConditions = (text: string) => {
    const textArray = text
      .split('·')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return textArray;
  };

  // eslint-disable-next-line no-plusplus
  /*   for (let i = 1; i <= maxPageNumber; i++) {
      pageNumbers.push(
        <li className={styles.pagination}>
          <button
            className={classnames({
              [styles.paginationButtonWDP]: true,
              [styles.paginationButtonActive]: currentPage === i,
            })}
            id={i.toString()}
            key={i}
            type="button"
            onClick={(e) => handlePageNumberClick(e.target)}>
            {i}
          </button>
        </li>,
      );
    } */
  const generatePages = () => {
    const pagesToShow = new Set<number>();
    if (isMobile) {
      // Mobile logic
      pagesToShow.add(1);
      pagesToShow.add(2);
      if (currentPage !== 1 && currentPage !== maxPageNumber) {
        pagesToShow.add(currentPage);
      }
      pagesToShow.add(maxPageNumber);
      pagesToShow.add(currentPage + 1);
      pagesToShow.add(currentPage - 1);
    } else if (maxPageNumber <= 20) {
      // Desktop: show all pages
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= maxPageNumber; i++) {
        pagesToShow.add(i);
      }
    } else {
      // Desktop: Smart condensed with ellipsis
      // eslint-disable-next-line no-plusplus
      for (let j = 1; j < 11; j++) {
        pagesToShow.add(j);
      }
      // eslint-disable-next-line no-plusplus
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        if (i > 2 && i < maxPageNumber - 1) {
          pagesToShow.add(i);
        }
      }
      pagesToShow.add(maxPageNumber - 1);
      pagesToShow.add(maxPageNumber);
    }
    const sortedPages = Array.from(pagesToShow)
      .filter((p) => p >= 1 && p <= maxPageNumber)
      .sort((a, b) => a - b);
    let lastPage = 0;
    sortedPages.forEach((page, index) => {
      if (page - lastPage > 1) {
        pageNumbers.push(
          // eslint-disable-next-line react/no-array-index-key
          <li key={`ellipsis-${index}`} className={styles.pagination}>
            <span className={styles.ellipsis}>...</span>
          </li>
        );
      }
      pageNumbers.push(
        <li key={page} className={styles.pagination}>
          <button
            className={classnames({
              [styles.paginationButtonWDP]: true,
              [styles.paginationButtonActive]: currentPage === page,
            })}
            id={page.toString()}
            type="button"
            onClick={(e) => handlePageNumberClick(e.target)}
          >
            {page}
          </button>
        </li>
      );
      lastPage = page;
    });
  };
  generatePages();

  return (
    <div>
      <div ref={targetRef} className={styles.searchResultHeader}>
        <div>
          {searchInput.trim()
          && (
            <div
              className={styles2.searchTitle}
              dangerouslySetInnerHTML={{ __html: `You searched for '${searchInput.trim()}'` }}
            />
          )}
          {/* eslint-disable-next-line no-nested-ternary */}
          {(telehealthResultsCount !== null && sponsorResultCount !== null)
            ? (
              <p>
                {sponsorResultCount}
                {' '}
                sponsor
                {sponsorResultCount === 1 ? ' was' : 's were' }
                {' '}
                found for the searched suburb
                <br />
                {telehealthResultsCount}
                {' '}
                telehealth sponsor
                {telehealthResultsCount === 1 ? ' was' : 's were'}
                {' '}
                found
              </p>
              )
              : (results.length === 1 ? (
                <p className={styles.summary}>
                  {results.length}
                  {' '}
                  sponsor was found
                </p>
              ) : (
                <p className={styles.summary}>
                  {results.length}
                  {' '}
                  sponsors were found
                </p>
              ))}
        </div>
        {
          results.length > 0
          && (
            <div id="wdp-search-result-accordion-expand-all-button">
              <Button size="medium" color="transparent" onClick={() => setAccordionOpen(!accordionsOpen)}>
                {accordionsOpen ? 'Collapse' : 'Expand'}
                {' '}
                all results
              </Button>
            </div>
          )
        }
      </div>
      {results.length === 0 && <Alert title="" message={noResultsError} theme="error" />}
      {paginatedResults.map((item) => (
        <div key={item.OrgName} className={styles.searchEachResultContainer}>
          <div className={styles.searchAddress}>
            <div className={styles2.searchTitle} dangerouslySetInnerHTML={{ __html: item.OrgName.replaceAll('\n', '<br />') }} />
            <div className={styles.searchSuburb}>
              <div className={styles2.searchTitle2}>
                {item.Suburb}
                ,
                {' '}
                {item.Postcode}
              </div>
            </div>
          </div>
          <div
            className={classnames({
              [styles.additionalInfoContainer]: true,
              'wdp-search-result-details-link': true,
            })}>
            {item.Website && (
              <div className={styles.info}>
                <p>
                  Website:
                  {' '}
                  <a className={styles.contactLink} target="_blank" rel="noreferrer" href={`${item.Website}`}>
                    {item.Website}
                  </a>
                </p>
              </div>
            )}
            {item.EmailAddress && (
              <div className={styles.info}>
                <p>
                  Email:
                  {' '}
                  <a href={`mailto:${item.EmailAddress}`} rel="noreferrer">
                    {item.EmailAddress}
                  </a>
                </p>
              </div>
            )}
            {item.PhoneNumber && (
              <div className={styles.info}>
                <p>
                  Phone:
                  {' '}
                  <a className={styles.contactLink} href={`tel:${item.PhoneNumber}`} title="Call this number">
                    {item.PhoneNumber}
                  </a>
                </p>
              </div>
            )}
          </div>
          <div
            className={classnames({
              [styles.accordionContainer]: true,
              'wdp-search-result-accordion': true,
            })}>
            {(item.Activities.length > 0 || item.Services.length > 0 || item.Telehealth || item.ReferralRequired.length > 0 || item.Conditions)
            && (
            <Accordion
              title="Details"
              variant="wdp"
              openAccordionOnClick={accordionsOpen}
              popupContent={(
                <div className={styles.searchResultConditionsContainer}>
                  {item.Activities.length > 0 && (
                    <div className={styles.searchResultActivityContainer}>
                      <div>
                        <p className={styles.searchResultSubHeader}>
                          <strong>Activity</strong>
                        </p>
                        <ul className={styles.searchResultList}>
                          {item.Activities.sort().map((activity) => (
                            <li key={activity} dangerouslySetInnerHTML={{ __html: activity.replaceAll('\n', '<br />') }} />
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {(item.Activities.length > 0 && (item.Services.length > 0 || (item.Telehealth || item.ReferralRequired.length > 0 || item.Conditions))) && !isMobile && <div className={styles.verticalLine} />}
                  {item.Services.length > 0 && (
                    <div className={styles.searchResultActivityContainer}>
                      <div>
                        <p className={styles.searchResultSubHeader}>
                          <strong>Services</strong>
                        </p>
                        <ul className={styles.searchResultList}>
                          {item.Services.sort().map((service) => (
                            <li key={service} dangerouslySetInnerHTML={{ __html: service.replaceAll('\n', '<br />') }} />
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {(item.Services.length > 0 && (item.Telehealth || item.ReferralRequired.length > 0 || item.Conditions)) && !isMobile && <div className={styles.verticalLine} />}
                  {(item.Telehealth || item.ReferralRequired.length > 0 || item.Conditions)
                  && (
                  <div className={styles.searchResultActivityContainer}>
                    {item.Telehealth && (
                      <div>
                        <p className={styles.searchResultSubHeader}>
                          <strong>Telehealth</strong>
                        </p>
                        <p className={styles.searchResultList}>{item.Telehealth}</p>
                      </div>
                    )}
                    {item.ReferralRequired.length > 0 && (
                      <div>
                        <p className={styles.searchResultSubHeader}>
                          <strong>Referral Required</strong>
                        </p>
                        {
                          item.ReferralRequired.length > 1
                          ? (
                            <ul className={styles.searchResultList}>
                              {item.ReferralRequired.sort().map((ref) => (
                                <li key={ref} dangerouslySetInnerHTML={{ __html: ref.replaceAll('\n', '<br />') }} />
                              ))}
                            </ul>
                          )
                          : <div dangerouslySetInnerHTML={{ __html: item.ReferralRequired[0].replaceAll('\n', '<br />') }} />
                        }
                      </div>
                    )}
                    {item.Conditions && (
                      <div>
                        <p className={styles.searchResultSubHeader}>
                          <strong>Conditions</strong>
                        </p>
                        {handleConditions(item.Conditions).map((condition) => (
                          // eslint-disable-next-line react/no-danger
                          <div className={styles.searchResultList} key={condition} dangerouslySetInnerHTML={{ __html: condition.replaceAll('\n', '<br />') }} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </div>
              )}
            />
            )}
          </div>
        </div>
      ))}
      {results.length > 0 && (
        <div className={styles.searchEachResultContainer}>
          <div className={styles.paginationContainer}>
            <button className={styles.paginationFirstLast} id="first" type="button" onClick={(e) => handleFirstLastClick(e, 1)} disabled={currentPage === 1}>
              <img
                src={sideArrowActive}
                className={styles.iconLeft}
                alt="Go to the first page" />
              First
            </button>
            <ul className={styles.paginationList}>{pageNumbers}</ul>
            <button
              className={styles.paginationFirstLast}
              id="last"
              key={maxPageNumber}
              type="button"
              onClick={(e) => handleFirstLastClick(e, maxPageNumber)}
              disabled={currentPage === maxPageNumber}>
              Last
              <img
                src={sideArrowActive}
                className={styles.icon}
                alt="Go to the last page" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
